import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

// Variants retuned to the AnyAPI design system (see src/styles/tokens.css).
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-10 border border-transparent text-sm font-medium whitespace-nowrap cursor-pointer transition-[opacity,border-color,color,background-color,scale] duration-150 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 active:not-disabled:scale-[0.96] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Ink on pale, the editorial primary. `--ink` is the near-black in
        // light and the pale grey in dark.
        primary: "bg-ink text-ink-fg hover:opacity-85",
        // The quiet secondary: a grey field, no hairline.
        line: "bg-tile text-text hover:bg-surface-3",
        ghost: "text-text-2 hover:text-text",
      },
      size: {
        default: "h-10 px-4",
        icon: "grid place-items-center p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "primary",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
